import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FileText, Save, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const HomepageServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [servicesSection, setServicesSection] = useState({
    title: 'Our Services',
    titleColor: '#1f2937',
    titleFontSize: '36',
    titleFontFamily: 'system-ui'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const [servicesRes, servicesSectionRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/services_info`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/business-info/services_section`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { info_value: '{}' } }))
      ]);

      const loadedServicesSection = JSON.parse(servicesSectionRes.data.info_value || '{}');
      setServicesSection({
        title: loadedServicesSection.title || 'Our Services',
        titleColor: loadedServicesSection.titleColor || '#1f2937',
        titleFontSize: loadedServicesSection.titleFontSize || '36',
        titleFontFamily: loadedServicesSection.titleFontFamily || 'system-ui'
      });

      const loadedServices = JSON.parse(servicesRes.data.info_value || '[]');
      setServices(loadedServices.map(s => ({
        title: s.title || '',
        description: s.description || '',
        price: s.price || '',
        titleColor: s.titleColor || '#1f2937',
        titleFontSize: s.titleFontSize || '24',
        titleFontFamily: s.titleFontFamily || 'system-ui',
        descriptionColor: s.descriptionColor || '#4b5563',
        descriptionFontSize: s.descriptionFontSize || '16',
        descriptionFontFamily: s.descriptionFontFamily || 'system-ui',
        priceColor: s.priceColor || '#2563eb',
        priceFontSize: s.priceFontSize || '18',
        priceFontFamily: s.priceFontFamily || 'system-ui'
      })));
    } catch (error) {
      console.error('Load data error:', error);
      // Set defaults
      setServicesSection({
        title: 'Our Services',
        titleColor: '#1f2937',
        titleFontSize: '36',
        titleFontFamily: 'system-ui'
      });
      setServices([
        { 
          title: 'Full Groom', 
          description: 'Complete grooming service including bath, haircut, nail trim, ear cleaning, and styling', 
          price: 'Starting at $72',
          titleColor: '#1f2937',
          titleFontSize: '24',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          priceColor: '#2563eb',
          priceFontSize: '18',
          priceFontFamily: 'system-ui'
        },
        { 
          title: 'Bath & Tidy', 
          description: 'Bath, brush out, nail trim, ear cleaning, and minor trimming', 
          price: 'Starting at $72',
          titleColor: '#1f2937',
          titleFontSize: '24',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          priceColor: '#2563eb',
          priceFontSize: '18',
          priceFontFamily: 'system-ui'
        },
        { 
          title: 'Specialty Cuts', 
          description: 'Breed-specific cuts and show preparation for your special pup', 
          price: 'Starting at $72',
          titleColor: '#1f2937',
          titleFontSize: '24',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          priceColor: '#2563eb',
          priceFontSize: '18',
          priceFontFamily: 'system-ui'
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const handleAddService = () => {
    setServices([...services, { 
      title: '', 
      description: '', 
      price: '',
      titleColor: '#1f2937',
      titleFontSize: '24',
      titleFontFamily: 'system-ui',
      descriptionColor: '#4b5563',
      descriptionFontSize: '16',
      descriptionFontFamily: 'system-ui',
      priceColor: '#2563eb',
      priceFontSize: '18',
      priceFontFamily: 'system-ui'
    }]);
  };

  const handleRemoveService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const handleSaveServices = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      console.log('[HomepageServicesEditor] Saving services:', services);
      console.log('[HomepageServicesEditor] Saving services section:', servicesSection);
      const responses = await Promise.all([
        axios.put(
          `${API_URL}/business-info/services_info`,
          { value: JSON.stringify(services) },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.put(
          `${API_URL}/business-info/services_section`,
          { value: JSON.stringify(servicesSection) },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      console.log('[HomepageServicesEditor] Save responses:', responses.map(r => r.data));
      toast.success('Homepage services updated successfully!');
    } catch (error) {
      console.error('Save services error:', error);
      toast.error('Failed to save services');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <FileText className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Homepage Services Display</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Edit the service cards shown on the homepage for marketing purposes. These are just text descriptions - to manage actual bookable services, see the "Bookable Services & Add-ons" section below.
      </p>

      {/* Section Title Editor */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-gray-700 mb-3">Section Title</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title Text</label>
            <input
              type="text"
              value={servicesSection.title}
              onChange={(e) => setServicesSection({ ...servicesSection, title: e.target.value })}
              className="input"
              placeholder="Our Services"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
              <select
                value={servicesSection.titleFontFamily}
                onChange={(e) => setServicesSection({ ...servicesSection, titleFontFamily: e.target.value })}
                className="input"
              >
                <option value="system-ui">System UI</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                <option value="Impact, fantasy">Impact</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                <option value="Verdana, sans-serif">Verdana</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
              <input
                type="number"
                value={servicesSection.titleFontSize}
                onChange={(e) => setServicesSection({ ...servicesSection, titleFontSize: e.target.value })}
                min="12"
                max="72"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={servicesSection.titleColor}
                  onChange={(e) => setServicesSection({ ...servicesSection, titleColor: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={servicesSection.titleColor}
                  onChange={(e) => setServicesSection({ ...servicesSection, titleColor: e.target.value })}
                  className="input flex-1"
                  placeholder="#1f2937"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {services.map((service, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-semibold text-gray-700">Service Card {index + 1}</h3>
              <button
                onClick={() => handleRemoveService(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={18} />
              </button>
            </div>

            {/* Content Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => handleServiceChange(index, 'title', e.target.value)}
                  className="input"
                  placeholder="e.g., Full Groom"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={service.description}
                  onChange={(e) => handleServiceChange(index, 'description', e.target.value)}
                  rows={2}
                  className="input resize-none"
                  placeholder="Brief description of the service"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Starting Price</label>
                <input
                  type="text"
                  value={service.price}
                  onChange={(e) => handleServiceChange(index, 'price', e.target.value)}
                  className="input"
                  placeholder="e.g., Starting at $72"
                />
              </div>
            </div>

            {/* Title Styling */}
            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Title Styling</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                  <select
                    value={service.titleFontFamily}
                    onChange={(e) => handleServiceChange(index, 'titleFontFamily', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="system-ui">System UI</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    <option value="Impact, fantasy">Impact</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Size (px)</label>
                  <input
                    type="number"
                    value={service.titleFontSize}
                    onChange={(e) => handleServiceChange(index, 'titleFontSize', e.target.value)}
                    min="12"
                    max="48"
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                  <div className="flex space-x-1">
                    <input
                      type="color"
                      value={service.titleColor}
                      onChange={(e) => handleServiceChange(index, 'titleColor', e.target.value)}
                      className="h-9 w-16 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={service.titleColor}
                      onChange={(e) => handleServiceChange(index, 'titleColor', e.target.value)}
                      className="input flex-1 text-sm"
                      placeholder="#1f2937"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Description Styling */}
            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description Styling</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                  <select
                    value={service.descriptionFontFamily}
                    onChange={(e) => handleServiceChange(index, 'descriptionFontFamily', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="system-ui">System UI</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    <option value="Impact, fantasy">Impact</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Size (px)</label>
                  <input
                    type="number"
                    value={service.descriptionFontSize}
                    onChange={(e) => handleServiceChange(index, 'descriptionFontSize', e.target.value)}
                    min="12"
                    max="32"
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                  <div className="flex space-x-1">
                    <input
                      type="color"
                      value={service.descriptionColor}
                      onChange={(e) => handleServiceChange(index, 'descriptionColor', e.target.value)}
                      className="h-9 w-16 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={service.descriptionColor}
                      onChange={(e) => handleServiceChange(index, 'descriptionColor', e.target.value)}
                      className="input flex-1 text-sm"
                      placeholder="#4b5563"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Price Styling */}
            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Price Styling</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                  <select
                    value={service.priceFontFamily}
                    onChange={(e) => handleServiceChange(index, 'priceFontFamily', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="system-ui">System UI</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    <option value="Impact, fantasy">Impact</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Size (px)</label>
                  <input
                    type="number"
                    value={service.priceFontSize}
                    onChange={(e) => handleServiceChange(index, 'priceFontSize', e.target.value)}
                    min="12"
                    max="32"
                    className="input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                  <div className="flex space-x-1">
                    <input
                      type="color"
                      value={service.priceColor}
                      onChange={(e) => handleServiceChange(index, 'priceColor', e.target.value)}
                      className="h-9 w-16 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={service.priceColor}
                      onChange={(e) => handleServiceChange(index, 'priceColor', e.target.value)}
                      className="input flex-1 text-sm"
                      placeholder="#2563eb"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleAddService}
          className="btn bg-gray-600 text-white hover:bg-gray-700 flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add Service Card</span>
        </button>

        <button
          onClick={handleSaveServices}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Homepage Services'}</span>
        </button>
      </div>
    </div>
  );
};

export default HomepageServicesEditor;
